import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBanknote,
  lucidePiggyBank,
  lucideRotateCcw,
  lucideTicket,
  lucideTicketCheck,
} from '@ng-icons/lucide';
import type { SalesStatistics } from '../../services/statistics.service';
import { formatCurrency } from '../../utils/currency.utils';
import { KpiCardComponent } from '../kpi-card/kpi-card.component';

@Component({
  selector: 'app-statistics',
  imports: [KpiCardComponent, NgIcon],
  providers: [
    provideIcons({
      lucideBanknote,
      lucidePiggyBank,
      lucideRotateCcw,
      lucideTicket,
      lucideTicketCheck,
    }),
  ],
  templateUrl: './statistics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './statistics.component.css',
})
export class StatisticsComponent {
  @Input() statistics: SalesStatistics | undefined = undefined;

  formatAmount(amount: number | undefined): string {
    return formatCurrency(amount);
  }

  /**
   * Internal (raffle/giveaway) vouchers are excluded from every figure above, because
   * no payment happened. They are surfaced as a "+ N intern" subtext on the card whose
   * number they qualify, so the omission is visible without a separate headline metric.
   * Returns undefined when there are none, so the card renders without a subtext.
   */
  private internalNote(value: number | undefined, formatted: string): string | undefined {
    return value ? `+ ${formatted} intern` : undefined;
  }

  get internalCountNote(): string | undefined {
    const count = this.statistics?.internal_vouchers_count;
    return this.internalNote(count, String(count));
  }

  get internalRedeemedNote(): string | undefined {
    const count = this.statistics?.internal_vouchers_redeemed;
    return this.internalNote(count, String(count));
  }

  get internalValueNote(): string | undefined {
    const value = this.statistics?.internal_vouchers_value;
    return this.internalNote(value, `${this.formatAmount(value)} €`);
  }

  get internalUnusedValueNote(): string | undefined {
    const value = this.statistics?.internal_vouchers_unused_value;
    return this.internalNote(value, `${this.formatAmount(value)} €`);
  }
}
